/**
 * KanbanColumn — a vertical stack of cards under a column header.
 *
 * Design round 3 (2026-05-15): column carries a user-picked color
 * (ADR-057 v1.1). The header shows a colored dot + name in matching
 * fg tint, and the whole column gets a very subtle background wash
 * of the same color. Selecting "default" keeps the column fully
 * monochrome.
 *
 * Hover-revealed affordances (delete column + add card) are kept.
 */

import type { Card, Column } from '@chaos/types/kanban';
import { useDroppable } from '@dnd-kit/core';
import { SortableContext, verticalListSortingStrategy } from '@dnd-kit/sortable';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { MoreHorizontal, Plus, Trash2 } from 'lucide-react';
import * as React from 'react';
import { useTranslation } from 'react-i18next';

import { kanbanApi, kanbanKeys } from '../../lib/api/kanban';
import { colorTint, normaliseColor, type StatusColor } from '../../lib/status-color';
import { cn } from '../../lib/utils';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '../ui/dropdown-menu';
import { StatusColorPicker } from './color-picker';
import { KanbanCard } from './kanban-card';

interface KanbanColumnProps {
  column: Column;
  cards: Card[];
  boardId: string;
  onAddCard: (columnId: string, title: string) => Promise<void> | void;
  onDeleteColumn?: (columnId: string) => Promise<void> | void;
  canMutate: boolean;
}

export function KanbanColumn({
  column,
  cards,
  boardId,
  onAddCard,
  onDeleteColumn,
  canMutate,
}: KanbanColumnProps) {
  const { t } = useTranslation();
  const qc = useQueryClient();
  const [adding, setAdding] = React.useState(false);
  const [draft, setDraft] = React.useState('');
  const inputRef = React.useRef<HTMLInputElement>(null);

  const { setNodeRef, isOver } = useDroppable({
    id: column.id,
    data: { type: 'column', column },
  });

  React.useEffect(() => {
    if (adding) inputRef.current?.focus();
  }, [adding]);

  const sortableIds = React.useMemo(() => cards.map((c) => c.id), [cards]);

  const colorKey = normaliseColor(column.color);
  const tint = colorTint(colorKey);

  const setColorM = useMutation({
    mutationFn: (color: StatusColor) =>
      kanbanApi.updateColumn(column.id, { color: color === 'default' ? null : color }),
    onMutate: async (color) => {
      // Optimistic — update columns cache so the colour switch feels instant.
      await qc.cancelQueries({ queryKey: kanbanKeys.boardColumns(boardId) });
      const prev = qc.getQueryData<Column[]>(kanbanKeys.boardColumns(boardId));
      if (prev) {
        const next = prev.map((c) =>
          c.id === column.id ? { ...c, color: color === 'default' ? null : color } : c,
        );
        qc.setQueryData(kanbanKeys.boardColumns(boardId), next);
      }
      return { prev };
    },
    onError: (_e, _v, ctx) => {
      if (ctx?.prev) qc.setQueryData(kanbanKeys.boardColumns(boardId), ctx.prev);
    },
    onSettled: () => {
      qc.invalidateQueries({ queryKey: kanbanKeys.boardColumns(boardId) });
    },
  });

  const submitDraft = async (): Promise<void> => {
    const trimmed = draft.trim();
    if (!trimmed) {
      setAdding(false);
      return;
    }
    await onAddCard(column.id, trimmed);
    setDraft('');
    // Keep adding mode so the user can chain inputs (common Trello UX).
  };

  return (
    <section
      ref={setNodeRef}
      className={cn(
        'group/column flex h-full w-72 shrink-0 flex-col rounded',
        'transition-colors',
        isOver && 'ring-1 ring-foreground/15',
      )}
      style={{
        backgroundColor:
          colorKey === 'default' ? undefined : `hsl(var(--status-${colorKey}-bg) / 0.45)`,
      }}
      aria-label={column.name}
    >
      {/* Header */}
      <header className="flex items-center justify-between gap-2 px-2.5 pt-1.5 pb-2">
        <div className="flex min-w-0 items-center gap-1.5">
          {canMutate ? (
            <StatusColorPicker
              value={column.color ?? null}
              onChange={(c) => setColorM.mutate(c)}
              ariaLabel={`Color of ${column.name}`}
            />
          ) : (
            <span
              aria-hidden
              className="h-2 w-2 rounded-full"
              style={{ backgroundColor: tint.foreground }}
            />
          )}
          <h3
            className="truncate text-[13px] font-semibold tracking-tight"
            style={{
              color: colorKey === 'default' ? undefined : tint.foreground,
            }}
          >
            {column.name}
          </h3>
          <span className="text-[11px] tabular-nums text-muted-foreground">{cards.length}</span>
        </div>
        {canMutate && (
          <div className="flex items-center gap-0.5">
            <button
              type="button"
              className="rounded p-1 text-muted-foreground transition-colors hover:bg-foreground/5 hover:text-foreground"
              onClick={() => setAdding(true)}
              aria-label={t('kanban.add_card')}
              title={t('kanban.add_card')}
            >
              <Plus className="h-3.5 w-3.5" />
            </button>
            {onDeleteColumn && (
              <DropdownMenu>
                <DropdownMenuTrigger asChild>
                  <button
                    type="button"
                    className="rounded p-1 text-muted-foreground opacity-0 transition-opacity hover:bg-foreground/5 hover:text-foreground group-hover/column:opacity-100"
                    aria-label="Column actions"
                  >
                    <MoreHorizontal className="h-3.5 w-3.5" />
                  </button>
                </DropdownMenuTrigger>
                <DropdownMenuContent align="end" className="w-44">
                  <DropdownMenuItem
                    onClick={() => onDeleteColumn(column.id)}
                    className="text-error hover:!text-error focus:!text-error"
                  >
                    <Trash2 className="h-4 w-4" />
                    <span>{t('kanban.delete_column')}</span>
                  </DropdownMenuItem>
                </DropdownMenuContent>
              </DropdownMenu>
            )}
          </div>
        )}
      </header>

      {/* Cards */}
      <div className="flex-1 overflow-y-auto pb-2">
        <div className="flex flex-col gap-1 px-1">
          <SortableContext items={sortableIds} strategy={verticalListSortingStrategy}>
            {cards.length === 0 && (
              <div
                className="px-2 py-3 text-center text-[12px] text-muted-foreground/70"
                aria-hidden
              >
                {t('kanban.empty_column_hint')}
              </div>
            )}
            {cards.map((card) => (
              <KanbanCard key={card.id} card={card} boardId={boardId} />
            ))}
          </SortableContext>
        </div>
      </div>

      {/* Add card */}
      {canMutate && (
        <div className="px-1 pb-1">
          {adding ? (
            <div className="flex flex-col gap-1.5 rounded bg-background p-1 shadow-[0_0_0_1px_hsl(var(--color-border))]">
              <input
                ref={inputRef}
                value={draft}
                onChange={(e) => setDraft(e.target.value)}
                onKeyDown={async (e) => {
                  if (e.key === 'Enter') {
                    e.preventDefault();
                    await submitDraft();
                  }
                  if (e.key === 'Escape') {
                    setDraft('');
                    setAdding(false);
                  }
                }}
                onBlur={() => {
                  if (!draft.trim()) setAdding(false);
                }}
                placeholder={t('kanban.new_card_placeholder')}
                className="w-full bg-transparent px-2 py-1.5 text-sm outline-none placeholder:text-muted-foreground"
                aria-label={t('kanban.new_card_placeholder')}
              />
            </div>
          ) : (
            <button
              type="button"
              onClick={() => setAdding(true)}
              className="flex w-full items-center gap-1.5 rounded px-2 py-1 text-[12px] text-muted-foreground opacity-0 transition-opacity hover:bg-foreground/5 hover:text-foreground group-hover/column:opacity-100"
            >
              <Plus className="h-3.5 w-3.5" />
              {t('kanban.add_card')}
            </button>
          )}
        </div>
      )}
    </section>
  );
}
